/**
 * Where a learned value came from.
 *
 * The `feed (computed)` chip is the honest special case: nothing was recorded, but the
 * value is provably equal to a merchant field, so the cockpit says so AND says it computed
 * that itself. It is visually distinct from a recorded feed origin — a computed claim must
 * never be mistaken for a stored one.
 */
import React from 'react';
import { originKind, type OriginKind } from '../lib/checks';
import { DEFS } from '../lib/stages';

const LABEL: Record<OriginKind, string> = {
  feed: 'feed', ai: 'ai', vendor: 'vendor', inferred: 'inferred ⚠', missing: 'unrecorded',
};

const TIP: Record<OriginKind, string> = {
  feed: DEFS.origin_feed,
  ai: DEFS.origin_ai,
  vendor: DEFS.origin_vendor,
  inferred: DEFS.origin_inferred,
  missing: DEFS.origin_missing,
};

interface OriginChipProps {
  origin: string | null | undefined;
  /** merchant field key that explains the value, when no origin was recorded */
  feedField?: string | null;
}

export function OriginChip({ origin, feedField }: OriginChipProps) {
  const kind = originKind(origin);
  if (kind === 'missing' && feedField) {
    return (
      <span
        className="pmc-chipv pmc-o-feed pmc-computed"
        data-tip={`${DEFS.origin_feed_match} (matches feed field "${feedField}")`}
      >
        feed
      </span>
    );
  }
  return (
    <span
      className={`pmc-chipv pmc-o-${kind}`}
      data-tip={TIP[kind] + (origin ? ` (recorded origin: "${origin}")` : '')}
    >
      {LABEL[kind]}
    </span>
  );
}
